<script setup lang="ts">
import { createNewVersionOfProForma } from '@/api/proFormas';
import SayErrorResponse from '@/mixins/sayErrorResponse';
import Say from '@/utils/Say'
import {inject, onMounted, ref, computed, watch} from "vue";
import {useI18n} from "vue-i18n";
import store from '@/store'
import { fetchClientContacts, fetchClient } from '@/api/clients'

const {t} = useI18n()

const emit = defineEmits(['approved', 'cancelForm', 'loading'])

const props = defineProps({
  invoice: {
    type: Object,
    required: true
  },
  job: {
    type: Object,
    required: true
  }
})

const clientId= ref(null as any)
const clientResponsibleId = ref(null as any)
const locationId = ref(null as any)

// Loading states
const loadingClient= ref(false)
const loadingClientContacts = ref(false)

// The list of client responsible contacts
const clientResponsibleItems = ref([] as any[])

// The list of clients in the store
const clients = computed(() => {
  const clients = store.getters['cache.clients']

  return clients
})

// The store client that matches the set clientID
const client = computed(() => {
  // If no Client ID then there can be no client
  if(!clientId.value) return null

  // Filter from the clients in the store
  return clients.value.find((client: any) => client.id === clientId.value)
})

// Location Items that is available in the currently selected client
const locationItems = computed(() => {
  return (client.value?.locations ?? []).filter((element: { id: null; location: string | string[]; latitude: any; longitude: any }) => {
    return !((element.location.includes('MISSING_LAT_LNG') || !element.latitude || !element.longitude));
  })
})

const form = ref(null as HTMLFormElement | null)
const formValid = ref(false)
const formRules = ref({
  clientId: [
    value => {
      if (value) return true
      return t('validationRequired')
    },
  ],
  clientResponsibleId: [
    value => {
      if (value || clientResponsibleId.value) return true
      return t('validationRequired')
    },
  ],
  locationId: [
    value => {
      if (value || locationId.value) return true
      return t('validationRequired')
    },
  ],
})
const copyContactToSelectedClient = ref(false)
const copyLocationToSelectedClient = ref(false)

const dialogTitle = inject('dialogTitle', null)
onMounted(() => {
  dialogTitle.value = t('createNewVersionOfProForma')
})

const formSubmitted = ref(false)
const submit = () => {
  if (form.value.validate() && !formValid.value) {
    return
  }
  
  if (formSubmitted.value) {
    return
  }
  formSubmitted.value = true
  emit('loading', true)

  createNewVersionOfProForma(props.invoice.id, {
    client_id: clientId.value,
    copy_contact_to_client: copyContactToSelectedClient.value,
    client_contact_id: !copyContactToSelectedClient.value ? clientResponsibleId.value : null,
    copy_location_to_client: copyLocationToSelectedClient.value,
    location_id: !copyLocationToSelectedClient.value ? locationId.value : null,
  })
    .then(response => {
      if (response.data.error) {
        SayErrorResponse(response);
        emit('loading', false)
        formSubmitted.value = false
      } else {
        setTimeout(() => {
          Say('success', t('newVersionOfProFormaCreatedSuccessfully'));
          emit('approved')
          formSubmitted.value = false
        }, 2000)
      }
    })
    .catch(error => {
      SayErrorResponse(error);
      formSubmitted.value = false
      emit('loading', false)
    });
}
const cancel = () => {
  emit('cancelForm')
}
defineExpose({submit, cancel})

const formatDate = (date: string) => {
  const [year, month, day] = date.split('-');
  return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
}

onMounted(async() => {
  await fetchClientContactsLocal()
  await initializeInputs()
  refreshClientFromServer()
})

const initializeInputs = async() => {
  // See if there is a clients list
  // If not the reload it
  if(clients.value.length === 0) {

    // set the clients list as dirty and the re-fetch
    store.commit('cache.clients.setDirty')
    await store.dispatch('cache.clients.fetch')

    // If still no clients then there is likely an issue in the API
    if(clients.value.length === 0) {

      alert(t('errorFetchingClients'))
      return;
    }
  }
}

watch(
  () => clientId.value,
  () => {
    clientResponsibleId.value = null;
    locationId.value = null;
    refreshClientFromServer()
    fetchClientContactsLocal()
  }
)

const fetchClientContactsLocal = async() => {
  if (clientId.value) {
    loadingClientContacts.value = true
    const response = await fetchClientContacts({ clientId: clientId.value })
    clientResponsibleItems.value = response.data || []
    loadingClientContacts.value = false
  } else {
    clientResponsibleItems.value = []
  }
}

// we want to re-fetch the client locations for this client
// and update the cache, since another user could have added
// a location since we cached the clients (we do this only once)
const refreshClientFromServer = async() => {
  if (!clientId.value) return

  const response = await fetchClient(clientId.value)

  client.value.locations = response.data.locations
}
</script>

<template>
  <div>
    <v-form
      v-model="formValid"
      ref="form"
      class="overflow-auto"
    >
      <v-row class="my-0">
        <v-col cols="6">
          <p class="mb-5" v-if="invoice.id">
            {{ t('period') }}:<br>
            <b>
              {{ formatDate(invoice.start_date) }}
              {{ t('upToAndIncludingAbbreviation') }}
              {{ formatDate(invoice.end_date) }}
            </b>
          </p>
          <p class="mb-5" v-if="invoice.id">
            {{ t('number') }}:<br>
            <b>
              {{ invoice.number }}
            </b>
          </p>
          <p class="mb-5" v-if="invoice.id">
            {{ t('addressedTo') }}:<br>
            <b>
              {{ invoice.addressee_line_one }}<br/>
              {{ invoice.addressee_line_two }}<br/>
              {{ invoice.addressee_address }}<br/>
              {{ invoice.addressee_postcode }} {{ invoice.addressee_city }}
            </b>
          </p>
          <p class="mb-5" v-if="invoice.id">
            {{ t('location') }}:<br>
            <b>
              {{ invoice.location }}
            </b>
          </p>
          <p>
            {{ t('byUpdatingProFormaTheJobWillAlsoBeUpdated') }}
          </p>
        </v-col>
        <v-col cols="6">
          <!-- Client -->
          <v-autocomplete
            v-model="clientId"
            :label="$t('client')"
            :loading="loadingClient"
            :items="clients"
            item-value="id"
            item-title="name"
            clearable
            variant="underlined"
            ref="nameInput"
            :rules="formRules.clientId"
          />

          <!-- Client contacts -->
          <p class="mb-2 mt-2">{{ t('clientResponsible') }}</p>
          <v-checkbox
            v-model="copyContactToSelectedClient"
            :label="t('copyClientResponsibleFromProFormaToSelectedClient')"
            hide-details
          />
          <v-autocomplete
            v-if="!copyContactToSelectedClient"
            :label="$t('clientResponsible')"
            :loading="loadingClientContacts"
            clearable
            :disabled="!clientId"
            v-model="clientResponsibleId"
            :items="clientResponsibleItems"
            item-value="id"
            item-title="name"
            variant="underlined"
            :rules="formRules.clientResponsibleId"
          />

          <!-- Location -->
          <p class="mb-2 mt-2">{{ t('location') }}</p>
          <v-checkbox
            v-model="copyLocationToSelectedClient"
            :label="t('copyLocationFromProFormaToSelectedClient')"
            hide-details
          />
          <v-autocomplete
            v-if="!copyLocationToSelectedClient"
            v-model="locationId"
            :items="locationItems"
            :disabled="!clientId"
            :label="$t('location')"
            :loading="loadingClient"
            item-title="location"
            item-value="id"
            variant="underlined"
            :rules="formRules.locationId"
          />
          
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>
